import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { useCurrency } from '~Hooks';
import { useExpressCart } from '../../Providers/ExpressCartProvider';
import { CartLineEdit } from '../../types';
import CartContents from './CartContents';
import CheckoutButton from './CheckoutButton';

/** Width of the docked cart on a landscape screen. */
export const CART_PANEL_WIDTH = 420;

/**
 * Right-hand column of the sheet, always on screen. It sits outside the
 * sliding track, so opening an item's detail pane never hides it.
 */
const Panel = styled.aside`
  flex: 0 0 ${CART_PANEL_WIDTH}px;
  width: ${CART_PANEL_WIDTH}px;
  /* A flex item's min-width is its content by default - a long title or the
     suggestion rail would widen the column. Clamp it. */
  min-width: 0;
  max-width: ${CART_PANEL_WIDTH}px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border-left: 1px solid var(--eposBorderColor);
  background: var(--eposSurface);
  color: var(--eposText);
`;

/** Rounded and inset. Smaller type and parts than the bar, so it fits the column. */
const PanelCheckout = styled(CheckoutButton)`
  flex: 0 0 auto;
  min-height: 100px;
  /* Tight under the promo row, so code and payment read as one stack. */
  margin: 0 20px 20px;
  padding: 0 28px;
  border-radius: 24px;
  gap: 14px;
  font-size: 26px;
  justify-content: space-between;

  /* The sum is the loudest thing on the button - the label only names the action. In em so useFitText scales it. */
  .epos-checkout-total {
    font-size: 1.33em;
  }

  .epos-checkout-arrow {
    width: 32px;
    height: 32px;
  }
`;

interface Props {
  onCheckout: () => void;
  /** "Ändra"/"Edit" on a cart line - opens ItemDetail prefilled with its picks. */
  onEditLine: (edit: CartLineEdit) => void;
  /** The line being edited, if any. */
  editing?: CartLineEdit;
}

/**
 * Landscape shell for the cart: a docked column with the contents on top and
 * the checkout button pinned underneath. No cart logic here - that is
 * CartContents, shared with the portrait CartDrawer.
 */
const CartPanel: FC<Props> = ({ onCheckout, onEditLine, editing }) => {
  const currency = useCurrency();
  const expressCart = useExpressCart();

  return (
    <Panel data-testid="epos-cart-panel">
      <CartContents onEditLine={onEditLine} editing={editing} />
      <PanelCheckout
        total={currency.format(expressCart.total - expressCart.discount)}
        originalTotal={currency.format(expressCart.total)}
        onCheckout={onCheckout}
        disabled={!expressCart.payable}
      />
    </Panel>
  );
};

export default memo(CartPanel);
